"use client";

import {usePostHog} from "@posthog/next";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import type {KeyboardEvent, MouseEvent} from "react";
import {useTranslation} from "react-i18next";
import {chunkTextForSpeech} from "@/utils/chat/readAloud";

interface UseGailGptReadAloudProps {
   text: string;
   conversationId?: string;
   messageId?: string;
   messageIndex?: number;
}

const AUDIO_MIME_TYPE = "audio/mpeg";
const READ_ALOUD_PLAYBACK_EVENT = "gailgpt-read-aloud-playback-started";
let readAloudInstanceCounter = 0;

function isAbortError(error: unknown): boolean {
   return error instanceof DOMException && error.name === "AbortError";
}

function announceReadAloudPlayback(instanceId: string): void {
   if (typeof window === "undefined") {
      return;
   }

   window.dispatchEvent(
      new CustomEvent(READ_ALOUD_PLAYBACK_EVENT, {
         detail: {instanceId},
      })
   );
}

function countWords(text: string): number {
   let count = 0;
   let inWord = false;

   for (const char of text.trim()) {
      if (char.trim() === "") {
         inWord = false;
         continue;
      }

      if (!inWord) {
         count += 1;
      }
      inWord = true;
   }

   return count;
}

export function useGailGptReadAloud({
   text,
   conversationId,
   messageId,
   messageIndex,
}: UseGailGptReadAloudProps) {
   const {t} = useTranslation("ask");
   const posthog = usePostHog();
   const chunks = useMemo(() => chunkTextForSpeech(text), [text]);
   const instanceIdRef = useRef("");
   const audioRef = useRef<HTMLAudioElement>(null);
   const progressRef = useRef<HTMLButtonElement>(null);
   const abortControllerRef = useRef<AbortController | null>(null);
   const appendQueueRef = useRef<Uint8Array[]>([]);
   const mediaSourceRef = useRef<MediaSource | null>(null);
   const objectUrlRef = useRef<string | null>(null);
   const sourceBufferRef = useRef<SourceBuffer | null>(null);
   const shouldPlayRef = useRef(false);
   const streamFinishedRef = useRef(false);
   // Accumulates streamed bytes so we can rebuild a single Blob once generation finishes.
   const audioBytesRef = useRef<Uint8Array[]>([]);
   // Cached fully-generated audio for this exact text. Reused on subsequent plays
   // so reopening the player after closing it does not re-hit ElevenLabs.
   const cachedBlobRef = useRef<Blob | null>(null);
   const cachedBlobTextRef = useRef<string>("");

   const [chunkCurrentTime, setChunkCurrentTime] = useState(0);
   const [generatedDuration, setGeneratedDuration] = useState(0);
   const [isExpanded, setIsExpanded] = useState(false);
   const [isPlaying, setIsPlaying] = useState(false);
   const [isGenerating, setIsGenerating] = useState(false);
   const [error, setError] = useState<string | null>(null);

   if (!instanceIdRef.current) {
      readAloudInstanceCounter += 1;
      instanceIdRef.current = `gailgpt-read-aloud-${readAloudInstanceCounter}`;
   }

   const canGenerateSpeech = chunks.length > 0;
   const currentAbsoluteTime = Math.min(generatedDuration || chunkCurrentTime, chunkCurrentTime);
   const progressPercentage =
      generatedDuration > 0 ? (currentAbsoluteTime / generatedDuration) * 100 : 0;
   const wordCount = useMemo(() => countWords(text), [text]);

   const updateGeneratedDuration = useCallback(() => {
      const audio = audioRef.current;
      if (!audio) {
         return;
      }

      const bufferedEnd =
         audio.buffered.length > 0 ? audio.buffered.end(audio.buffered.length - 1) : 0;
      const finiteDuration = Number.isFinite(audio.duration) ? audio.duration : 0;
      const nextDuration = Math.max(bufferedEnd, finiteDuration);
      if (nextDuration > 0) {
         setGeneratedDuration((current) => Math.max(current, nextDuration));
      }
   }, []);

   const playAudio = useCallback(async () => {
      const audio = audioRef.current;
      if (!audio) {
         return;
      }

      try {
         await audio.play();
         shouldPlayRef.current = true;
         setIsPlaying(true);
      } catch (playError) {
         if (!isAbortError(playError)) {
            console.error("Failed to play GailGPT speech", playError);
            setError(t("readAloud.playFailed"));
         }
      }
   }, [t]);

   const handlePlaybackPromise = useCallback((playbackPromise: Promise<void>) => {
      playbackPromise.catch((playError) => {
         if (!isAbortError(playError)) {
            console.error("Failed to play GailGPT speech", playError);
         }
      });
   }, []);

   const requestPlayAudio = useCallback(() => {
      handlePlaybackPromise(playAudio());
   }, [handlePlaybackPromise, playAudio]);

   const pauseForCompetingPlayback = useCallback((event: Event) => {
      const {instanceId} = (event as CustomEvent<{instanceId?: string}>).detail ?? {};
      if (instanceId === instanceIdRef.current) {
         return;
      }

      shouldPlayRef.current = false;
      audioRef.current?.pause();
      setIsPlaying(false);
   }, []);

   const clearAudioSource = useCallback(() => {
      const audio = audioRef.current;
      if (audio) {
         audio.pause();
         audio.removeAttribute("src");
         audio.load();
      }

      if (objectUrlRef.current) {
         URL.revokeObjectURL(objectUrlRef.current);
         objectUrlRef.current = null;
      }

      appendQueueRef.current = [];
      mediaSourceRef.current = null;
      sourceBufferRef.current = null;
      streamFinishedRef.current = false;
   }, []);

   const finishMediaSourceIfReady = useCallback(() => {
      const mediaSource = mediaSourceRef.current;
      const sourceBuffer = sourceBufferRef.current;
      if (
         !streamFinishedRef.current ||
         !mediaSource ||
         !sourceBuffer ||
         mediaSource.readyState !== "open" ||
         sourceBuffer.updating ||
         appendQueueRef.current.length > 0
      ) {
         return;
      }

      try {
         mediaSource.endOfStream();
      } catch {
         // The media source can already be closing if the user collapses the player.
      }
      updateGeneratedDuration();
      setIsGenerating(false);
   }, [updateGeneratedDuration]);

   const appendNextQueuedBuffer = useCallback(() => {
      const mediaSource = mediaSourceRef.current;
      const sourceBuffer = sourceBufferRef.current;
      if (!mediaSource || !sourceBuffer || mediaSource.readyState !== "open" || sourceBuffer.updating) {
         return;
      }

      const nextBuffer = appendQueueRef.current.shift();
      if (!nextBuffer) {
         finishMediaSourceIfReady();
         return;
      }

      try {
         sourceBuffer.appendBuffer(new Uint8Array(nextBuffer).buffer);
         if (shouldPlayRef.current && audioRef.current?.paused) {
            requestPlayAudio();
         }
      } catch (appendError) {
         console.error("Failed to append GailGPT speech audio", appendError);
         setError(t("readAloud.failed"));
         setIsGenerating(false);
      }
   }, [finishMediaSourceIfReady, requestPlayAudio, t]);

   const enqueueAudioBuffer = useCallback(
      (buffer: Uint8Array) => {
         appendQueueRef.current.push(buffer);
         appendNextQueuedBuffer();
      },
      [appendNextQueuedBuffer]
   );

   const streamSpeechChunk = useCallback(
      async (speechText: string, controller: AbortController): Promise<void> => {
         const response = await fetch("/api/gailgpt/speech", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({text: speechText}),
            signal: controller.signal,
         });

         if (!response.ok || !response.body) {
            throw new Error(`Speech request failed with ${response.status}`);
         }

         const reader = response.body.getReader();
         while (true) {
            const {done, value} = await reader.read();
            if (done) {
               break;
            }

            if (value) {
               audioBytesRef.current.push(value);
               enqueueAudioBuffer(value);
            }
         }
      },
      [enqueueAudioBuffer]
   );

   const playBlob = useCallback(
      async (blob: Blob): Promise<void> => {
         const audio = audioRef.current;
         if (!audio) {
            return;
         }

         const objectUrl = URL.createObjectURL(blob);
         objectUrlRef.current = objectUrl;
         audio.src = objectUrl;
         setIsGenerating(false);
         await playAudio();
      },
      [playAudio]
   );

   const playBlobFallback = useCallback(
      async (controller: AbortController): Promise<void> => {
         const speechBlobs: Blob[] = [];
         for (const chunk of chunks) {
            const response = await fetch("/api/gailgpt/speech", {
               method: "POST",
               headers: {"Content-Type": "application/json"},
               body: JSON.stringify({text: chunk}),
               signal: controller.signal,
            });

            if (!response.ok) {
               throw new Error(`Speech request failed with ${response.status}`);
            }

            speechBlobs.push(await response.blob());
         }

         const blob = new Blob(speechBlobs, {type: AUDIO_MIME_TYPE});
         cachedBlobRef.current = blob;
         cachedBlobTextRef.current = text;
         await playBlob(blob);
      },
      [chunks, playBlob, text]
   );

   const cacheGeneratedAudio = useCallback(() => {
      if (audioBytesRef.current.length === 0) {
         return;
      }

      cachedBlobRef.current = new Blob(
         audioBytesRef.current.map((bytes) => new Uint8Array(bytes)),
         {type: AUDIO_MIME_TYPE}
      );
      cachedBlobTextRef.current = text;
   }, [text]);

   const handleStreamError = useCallback(
      (streamError: unknown) => {
         if (isAbortError(streamError)) {
            return;
         }

         console.error("Failed to stream GailGPT speech", streamError);
         setError(t("readAloud.failed"));
         setIsGenerating(false);
      },
      [t]
   );

   const streamSpeechChunks = useCallback(
      async (controller: AbortController): Promise<void> => {
         try {
            for (const chunk of chunks) {
               await streamSpeechChunk(chunk, controller);
            }
            streamFinishedRef.current = true;
            cacheGeneratedAudio();
            appendNextQueuedBuffer();
         } catch (streamError) {
            handleStreamError(streamError);
         }
      },
      [appendNextQueuedBuffer, cacheGeneratedAudio, chunks, handleStreamError, streamSpeechChunk]
   );

   const handleSourceBufferUpdateEnd = useCallback(() => {
      updateGeneratedDuration();
      if (shouldPlayRef.current && audioRef.current?.paused) {
         requestPlayAudio();
      }
      appendNextQueuedBuffer();
   }, [appendNextQueuedBuffer, requestPlayAudio, updateGeneratedDuration]);

   const initializeSourceBuffer = useCallback(
      (mediaSource: MediaSource): boolean => {
         try {
            const sourceBuffer = mediaSource.addSourceBuffer(AUDIO_MIME_TYPE);
            sourceBuffer.mode = "sequence";
            sourceBufferRef.current = sourceBuffer;
            sourceBuffer.addEventListener("updateend", handleSourceBufferUpdateEnd);
            return true;
         } catch (sourceError) {
            console.error("Failed to initialize GailGPT speech stream", sourceError);
            setError(t("readAloud.failed"));
            setIsGenerating(false);
            return false;
         }
      },
      [handleSourceBufferUpdateEnd, t]
   );

   const canUseMediaSource = useCallback(() => {
      if (typeof window === "undefined" || !("MediaSource" in window)) {
         return false;
      }

      const mediaSource = window.MediaSource;
      if (!mediaSource) {
         return false;
      }

      return (
         typeof mediaSource.isTypeSupported !== "function" ||
         mediaSource.isTypeSupported(AUDIO_MIME_TYPE)
      );
   }, []);

   const trackPlayClicked = useCallback(
      (playbackSource: "start" | "resume") => {
         posthog?.capture("gailgpt_read_aloud_play_clicked", {
            conversation_id: conversationId,
            message_id: messageId,
            message_index: messageIndex,
            text_length: text.length,
            word_count: wordCount,
            speech_chunk_count: chunks.length,
            playback_source: playbackSource,
            is_cached: cachedBlobRef.current !== null && cachedBlobTextRef.current === text,
            generated_duration_seconds: generatedDuration || undefined,
         });
      },
      [chunks.length, conversationId, generatedDuration, messageId, messageIndex, posthog, text, wordCount]
   );

   const startPlayback = useCallback(async () => {
      if (!canGenerateSpeech) {
         setError(t("readAloud.unavailable"));
         return;
      }

      announceReadAloudPlayback(instanceIdRef.current);
      abortControllerRef.current?.abort();
      clearAudioSource();
      setChunkCurrentTime(0);
      setGeneratedDuration(0);
      setIsExpanded(true);
      setError(null);
      shouldPlayRef.current = true;

      if (cachedBlobRef.current && cachedBlobTextRef.current === text) {
         setIsGenerating(false);
         try {
            await playBlob(cachedBlobRef.current);
         } catch (playError) {
            console.error("Failed to replay cached GailGPT speech", playError);
            setError(t("readAloud.failed"));
         }
         return;
      }

      audioBytesRef.current = [];
      setIsGenerating(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (!canUseMediaSource()) {
         try {
            await playBlobFallback(controller);
         } catch (streamError) {
            if (!isAbortError(streamError)) {
               console.error("Failed to generate GailGPT speech", streamError);
               setError(t("readAloud.failed"));
               setIsGenerating(false);
            }
         }
         return;
      }

      const mediaSource = new MediaSource();
      mediaSourceRef.current = mediaSource;

      const objectUrl = URL.createObjectURL(mediaSource);
      objectUrlRef.current = objectUrl;
      if (audioRef.current) {
         audioRef.current.src = objectUrl;
      }

      mediaSource.addEventListener(
         "sourceopen",
         () => {
            if (abortControllerRef.current !== controller || mediaSource.readyState !== "open") {
               return;
            }

            if (!initializeSourceBuffer(mediaSource)) {
               setIsGenerating(false);
               return;
            }

            handlePlaybackPromise(streamSpeechChunks(controller));
         },
         {once: true}
      );
   }, [
      appendNextQueuedBuffer,
      canGenerateSpeech,
      canUseMediaSource,
      chunks,
      clearAudioSource,
      handlePlaybackPromise,
      initializeSourceBuffer,
      playAudio,
      playBlob,
      playBlobFallback,
      streamSpeechChunks,
      t,
      text,
   ]);

   const togglePlayPause = useCallback(async () => {
      if (isPlaying) {
         shouldPlayRef.current = false;
         audioRef.current?.pause();
         setIsPlaying(false);
         return;
      }

      if (!isExpanded || !audioRef.current?.src) {
         trackPlayClicked("start");
         await startPlayback();
         return;
      }

      announceReadAloudPlayback(instanceIdRef.current);
      shouldPlayRef.current = true;
      trackPlayClicked("resume");
      await playAudio();
   }, [isExpanded, isPlaying, playAudio, startPlayback, trackPlayClicked]);

   const collapsePlayer = useCallback(() => {
      shouldPlayRef.current = false;
      abortControllerRef.current?.abort();
      clearAudioSource();
      setIsPlaying(false);
      setIsGenerating(false);
      setIsExpanded(false);
      setChunkCurrentTime(0);
      setGeneratedDuration(0);
      setError(null);
   }, [clearAudioSource]);

   useEffect(() => {
      window.addEventListener(READ_ALOUD_PLAYBACK_EVENT, pauseForCompetingPlayback);

      return () => {
         window.removeEventListener(READ_ALOUD_PLAYBACK_EVENT, pauseForCompetingPlayback);
      };
   }, [pauseForCompetingPlayback]);

   useEffect(() => {
      if (cachedBlobTextRef.current && cachedBlobTextRef.current !== text) {
         cachedBlobRef.current = null;
         cachedBlobTextRef.current = "";
         audioBytesRef.current = [];
      }
   }, [text]);

   useEffect(() => {
      return () => {
         abortControllerRef.current?.abort();
         clearAudioSource();
      };
   }, [clearAudioSource]);

   const seekToGeneratedTime = useCallback((targetTime: number) => {
      if (!audioRef.current || generatedDuration <= 0) {
         return;
      }

      const audio = audioRef.current;
      const nextTime = Math.min(Math.max(0, targetTime), generatedDuration);
      audio.currentTime = nextTime;
      setChunkCurrentTime(nextTime);

      if (shouldPlayRef.current) {
         requestPlayAudio();
      }
   }, [generatedDuration, requestPlayAudio]);

   const handleProgressClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
         if (!progressRef.current || generatedDuration <= 0) {
            return;
         }

         const rect = progressRef.current.getBoundingClientRect();
         const clickX = event.clientX - rect.left;
         const percentage = Math.max(0, Math.min(1, clickX / rect.width));
         seekToGeneratedTime(percentage * generatedDuration);
      },
      [generatedDuration, seekToGeneratedTime]
   );

   const handleProgressKeyDown = useCallback(
      (event: KeyboardEvent<HTMLButtonElement>) => {
         if (generatedDuration <= 0) {
            return;
         }

         const seekStepSeconds = 5;
         const seekPageStepSeconds = 15;
         let nextTime: number | null = null;

         if (event.key === "ArrowLeft") {
            nextTime = currentAbsoluteTime - seekStepSeconds;
         } else if (event.key === "ArrowRight") {
            nextTime = currentAbsoluteTime + seekStepSeconds;
         } else if (event.key === "PageDown") {
            nextTime = currentAbsoluteTime - seekPageStepSeconds;
         } else if (event.key === "PageUp") {
            nextTime = currentAbsoluteTime + seekPageStepSeconds;
         } else if (event.key === "Home") {
            nextTime = 0;
         } else if (event.key === "End") {
            nextTime = generatedDuration;
         }

         if (nextTime === null) {
            return;
         }

         event.preventDefault();
         seekToGeneratedTime(nextTime);
      },
      [currentAbsoluteTime, generatedDuration, seekToGeneratedTime]
   );

   const jumpBy = useCallback(
      (seconds: number) => {
         seekToGeneratedTime(currentAbsoluteTime + seconds);
      },
      [currentAbsoluteTime, seekToGeneratedTime]
   );

   useEffect(() => {
      const audio = audioRef.current;
      if (!audio) {
         return;
      }

      const handleTimeUpdate = () => {
         setChunkCurrentTime(audio.currentTime);
         updateGeneratedDuration();
      };
      const handlePause = () => setIsPlaying(false);
      const handlePlay = () => setIsPlaying(true);
      const handleDurationUpdate = () => updateGeneratedDuration();
      const handleEnded = () => {
         if (!isGenerating) {
            shouldPlayRef.current = false;
            setIsPlaying(false);
         }
      };

      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("pause", handlePause);
      audio.addEventListener("play", handlePlay);
      audio.addEventListener("durationchange", handleDurationUpdate);
      audio.addEventListener("loadedmetadata", handleDurationUpdate);
      audio.addEventListener("progress", handleDurationUpdate);
      audio.addEventListener("ended", handleEnded);

      return () => {
         audio.removeEventListener("timeupdate", handleTimeUpdate);
         audio.removeEventListener("pause", handlePause);
         audio.removeEventListener("play", handlePlay);
         audio.removeEventListener("durationchange", handleDurationUpdate);
         audio.removeEventListener("loadedmetadata", handleDurationUpdate);
         audio.removeEventListener("progress", handleDurationUpdate);
         audio.removeEventListener("ended", handleEnded);
      };
   }, [isGenerating, updateGeneratedDuration]);

   return {
      audioRef,
      progressRef,
      canGenerateSpeech,
      collapsePlayer,
      currentAbsoluteTime,
      error,
      generatedDuration,
      handleProgressClick,
      handleProgressKeyDown,
      isExpanded,
      isGenerating,
      isPlaying,
      jumpBy,
      progressPercentage,
      togglePlayPause,
   };
}
